import { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Building2, GraduationCap, Plus, ShieldCheck, Trash2, ImageIcon, FileText, Pencil, Power, Palette, Users, Search, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { fileUrl } from "@/components/Certificate";
import { SkeletonStatCards, SkeletonTable } from "@/components/PageSkeleton";
import CertificateDesignDialog from "@/components/CertificateDesignDialog";
import StaffManagerDialog from "@/components/StaffManagerDialog";
import { usePaginatedList, Pagination } from "@/components/Pagination";
import { toast } from "sonner";

const emptyForm = {
  name: "", address: "", contact: "", email: "",
  year_established: new Date().getFullYear(),
  president: "",
  category: "University",
  admin_name: "", admin_email: "", admin_password: "",
};

export default function SuperAdminDashboard() {
  const [institutions, setInstitutions] = useState([]);
  // Search + paginate the institutions list — max 15 per page, live substring
  // filter across the fields a super-admin would typically type.
  const instPage = usePaginatedList(institutions, {
    pageSize: 15,
    searchFields: ["name", "president", "email", "address", "category"],
  });
  const [stats, setStats] = useState({ total_institutions: 0, total_graduates: 0 });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [logoFile, setLogoFile] = useState(null);
  const [templateFile, setTemplateFile] = useState(null);
  const [saving, setSaving] = useState(false);
  // Edit dialog
  const [editInst, setEditInst] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  // Level-2 admin management
  const [l2Admins, setL2Admins] = useState([]);
  const [l2Open, setL2Open] = useState(false);
  const [l2Form, setL2Form] = useState({ email: "", name: "", password: "" });
  const [savingL2, setSavingL2] = useState(false);
  const [loading, setLoading] = useState(true);
  // Certificate design dialog
  const [designInst, setDesignInst] = useState(null);
  const [staffOpen, setStaffOpen] = useState(false);

  const reload = async () => {
    try {
      const [i, s, a] = await Promise.all([
        api.get("/institutions"),
        api.get("/stats"),
        api.get("/admins/l2"),
      ]);
      setInstitutions(i.data);
      setStats(s.data);
      setL2Admins(a.data);
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount only. `reload` is redefined every render and isn't
  // memoized — adding it to deps would create an infinite refetch loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, []);

  const createL2Admin = async (e) => {
    e.preventDefault();
    if (!l2Form.email || !l2Form.password || !l2Form.name) return;
    if (l2Form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setSavingL2(true);
    try {
      await api.post("/admins/l2", l2Form);
      toast.success(`Level-2 admin ${l2Form.email} created`);
      setL2Open(false);
      setL2Form({ email: "", name: "", password: "" });
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to create admin");
    } finally {
      setSavingL2(false);
    }
  };

  const deleteL2Admin = async (adminId, email) => {
    if (!window.confirm(`Delete Level-2 admin ${email}? They will lose access immediately.`)) return;
    try {
      await api.delete(`/admins/l2/${adminId}`);
      toast.success("Level-2 admin removed");
      reload();
    } catch (e) {
      toast.error("Failed to remove admin");
    }
  };

  const uploadAsset = async (instId, kind, file) => {
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", file);
    await api.post(`/institutions/${instId}/assets`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/institutions", { ...form, year_established: Number(form.year_established) });
      if (logoFile) {
        try { await uploadAsset(data.id, "logo", logoFile); }
        catch { toast.error("Logo upload failed (institution created)"); }
      }
      if (templateFile) {
        try { await uploadAsset(data.id, "template", templateFile); }
        catch { toast.error("Template upload failed (institution created)"); }
      }
      toast.success("Institution onboarded");
      setOpen(false);
      setForm(emptyForm);
      setLogoFile(null);
      setTemplateFile(null);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This removes all certificates, vouchers, and admins.`)) return;
    try {
      await api.delete(`/institutions/${id}`);
      toast.success("Institution removed");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const toggleActive = async (inst) => {
    try {
      const next = !inst.active;
      await api.patch(`/institutions/${inst.id}`, { active: next });
      toast.success(next ? "Institution activated" : "Institution deactivated");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const openEdit = (inst) => setEditInst({ ...inst });

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editInst) return;
    setSavingEdit(true);
    try {
      const payload = {
        name: editInst.name,
        address: editInst.address,
        contact: editInst.contact,
        email: editInst.email,
        year_established: Number(editInst.year_established),
        president: editInst.president,
        category: editInst.category,
      };
      await api.patch(`/institutions/${editInst.id}`, payload);
      toast.success("Institution updated");
      setEditInst(null);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <DashboardLayout title="Network overview" subtitle="Manage institutions, monitor credentials, and onboard new partners.">
      {/* Quick actions */}
      <div className="mb-6 flex justify-end">
        <Button variant="outline" onClick={() => setStaffOpen(true)} data-testid="manage-staff-btn">
          <Users className="w-4 h-4 mr-2" /> Manage team
        </Button>
      </div>

      {/* Stats */}
      {loading ? (
        <div className="mb-10">
          <SkeletonStatCards count={3} />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" data-testid="stats-grid">
          <StatCard icon={Building2} label="Total institutions" value={stats.total_institutions} testId="stat-total-institutions" />
          <StatCard icon={GraduationCap} label="Total graduates" value={stats.total_graduates} testId="stat-total-graduates" />
          <StatCard icon={ShieldCheck} label="Avg. graduates / institution"
            value={stats.total_institutions ? Math.round(stats.total_graduates / stats.total_institutions) : 0}
            testId="stat-avg-grads"
          />
        </div>
      )}

      {/* Institutions table */}
      <Card className="border-slate-200">
        <div className="p-6 flex flex-wrap items-center justify-between gap-4 border-b border-slate-200">
          <div>
            <h2 className="font-display font-bold text-xl text-slate-900">Institutions</h2>
            <p className="text-sm text-slate-500 mt-0.5">All partner institutions and their graduate counts.</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative w-full sm:w-72">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                type="text"
                value={instPage.query}
                onChange={(e) => instPage.setQuery(e.target.value)}
                placeholder="Search by name, president, email…"
                className="h-10 pl-9 pr-9"
                data-testid="admin-inst-search-input"
                aria-label="Search institutions"
              />
              {instPage.query && (
                <button
                  type="button"
                  onClick={() => instPage.setQuery("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1"
                  aria-label="Clear search"
                  data-testid="admin-inst-search-clear"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 font-display font-semibold" data-testid="add-institution-btn">
                <Plus className="w-4 h-4 mr-2" /> Onboard institution
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto dialog-scroll-hint">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Onboard institution</DialogTitle>
                <DialogDescription>Creates the institution profile and the first institution admin account.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-6 pt-2">
                <div className="space-y-3">
                  <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Institution</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="inst-name-input" />
                    <Field label="Year established" type="number" required value={form.year_established} onChange={(v) => setForm({ ...form, year_established: v })} testId="inst-year-input" />
                  </div>
                  <Field label="Address" required value={form.address} onChange={(v) => setForm({ ...form, address: v })} testarea testId="inst-address-input" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Contact" required value={form.contact} onChange={(v) => setForm({ ...form, contact: v })} testId="inst-contact-input" />
                    <Field label="Email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="inst-email-input" />
                  </div>
                  <Field label="President / Principal" required value={form.president} onChange={(v) => setForm({ ...form, president: v })} testId="inst-president-input" />
                  <div>
                    <Label className="text-sm font-medium text-slate-600 mb-1.5 block">Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger className="h-11" data-testid="inst-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="High School">High School</SelectItem>
                        <SelectItem value="University">University</SelectItem>
                        <SelectItem value="Professional School">Professional School</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Institution admin</div>
                  <Field label="Admin name" required value={form.admin_name} onChange={(v) => setForm({ ...form, admin_name: v })} testId="admin-name-input" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Admin email" type="email" required value={form.admin_email} onChange={(v) => setForm({ ...form, admin_email: v })} testId="admin-email-input" />
                    <Field label="Temporary password" type="password" required value={form.admin_password} onChange={(v) => setForm({ ...form, admin_password: v })} testId="admin-password-input" />
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Branding (optional)</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <FileDrop
                      label="Institution logo"
                      icon={ImageIcon}
                      hint="PNG, JPEG, SVG · displayed on cards + certificates"
                      accept="image/png,image/jpeg,image/webp"
                      file={logoFile}
                      onFile={setLogoFile}
                      testId="upload-logo-input"
                    />
                    <FileDrop
                      label="Certificate template"
                      icon={FileText}
                      hint="PNG/JPEG (recommended) or PDF · used as certificate design"
                      accept="image/png,image/jpeg,image/webp,application/pdf"
                      file={templateFile}
                      onFile={setTemplateFile}
                      testId="upload-template-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-institution-btn">
                    {saving ? "Saving…" : "Create institution"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>

        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Year</TableHead>
              <TableHead>President</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead className="text-right">Graduates</TableHead>
              <TableHead className="text-center">Active</TableHead>
              <TableHead className="w-20">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="institutions-table-body">
            {loading && (
              <TableRow>
                <TableCell colSpan={8} className="p-0">
                  <SkeletonTable rows={4} cols={7} testId="institutions-skeleton" />
                </TableCell>
              </TableRow>
            )}
            {!loading && instPage.totalFiltered === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-slate-500 py-12">
                  {instPage.query.trim()
                    ? `No institutions match "${instPage.query.trim()}".`
                    : "No institutions yet. Onboard your first partner above."}
                </TableCell>
              </TableRow>
            )}
            {!loading && instPage.items.map((inst) => (
              <TableRow key={inst.id} data-testid={`inst-row-${inst.id}`} className={inst.active === false ? "opacity-60" : ""}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {inst.logo_path && (
                      <img src={fileUrl(inst.logo_path)} alt="" className="w-8 h-8 object-contain rounded border border-slate-200 bg-white p-0.5" />
                    )}
                    <div>
                      <div className="font-medium text-slate-900">{inst.name}</div>
                      <div className="text-xs text-slate-500 truncate max-w-xs">{inst.address}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell><span className="text-xs font-semibold uppercase tracking-widest text-slate-500">{inst.category}</span></TableCell>
                <TableCell className="text-slate-600">{inst.year_established}</TableCell>
                <TableCell className="text-slate-600">{inst.president}</TableCell>
                <TableCell>
                  <div className="text-sm text-slate-700">{inst.email}</div>
                  <div className="text-xs text-slate-500">{inst.contact}</div>
                </TableCell>
                <TableCell className="text-right">
                  <span className="font-display font-extrabold text-lg text-slate-900">{inst.graduate_count}</span>
                </TableCell>
                <TableCell className="text-center">
                  <Switch checked={inst.active !== false} onCheckedChange={() => toggleActive(inst)} data-testid={`toggle-active-${inst.id}`} aria-label="Toggle active" />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => setDesignInst(inst)} data-testid={`design-inst-${inst.id}`} title="Certificate design">
                      <Palette className="w-4 h-4 text-slate-500 hover:text-blue-600" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(inst)} data-testid={`edit-inst-${inst.id}`} title="Edit institution">
                      <Pencil className="w-4 h-4 text-slate-500 hover:text-blue-600" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(inst.id, inst.name)} data-testid={`delete-inst-${inst.id}`} title="Delete institution">
                      <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-600" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!loading && (
          <Pagination
            page={instPage.page}
            totalPages={instPage.totalPages}
            setPage={instPage.setPage}
            rangeStart={instPage.rangeStart}
            rangeEnd={instPage.rangeEnd}
            totalFiltered={instPage.totalFiltered}
            testId="admin-inst-pagination"
          />
        )}
      </Card>

      {/* Edit institution dialog */}
      <Dialog open={!!editInst} onOpenChange={(o) => !o && setEditInst(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto dialog-scroll-hint">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Edit institution</DialogTitle>
            <DialogDescription>Update institution details. Slug will update automatically if the name changes.</DialogDescription>
          </DialogHeader>
          {editInst && (
            <form onSubmit={handleSaveEdit} className="space-y-4 pt-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Name" required value={editInst.name} onChange={(v) => setEditInst({ ...editInst, name: v })} testId="edit-inst-name" />
                <Field label="Year established" type="number" required value={editInst.year_established} onChange={(v) => setEditInst({ ...editInst, year_established: v })} testId="edit-inst-year" />
              </div>
              <Field label="Address" required value={editInst.address} onChange={(v) => setEditInst({ ...editInst, address: v })} testarea testId="edit-inst-address" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Contact" required value={editInst.contact} onChange={(v) => setEditInst({ ...editInst, contact: v })} testId="edit-inst-contact" />
                <Field label="Email" type="email" required value={editInst.email} onChange={(v) => setEditInst({ ...editInst, email: v })} testId="edit-inst-email" />
              </div>
              <Field label="President / Principal" required value={editInst.president} onChange={(v) => setEditInst({ ...editInst, president: v })} testId="edit-inst-president" />
              <div>
                <Label className="text-sm font-medium text-slate-600 mb-1.5 block">Category</Label>
                <Select value={editInst.category || "University"} onValueChange={(v) => setEditInst({ ...editInst, category: v })}>
                  <SelectTrigger className="h-11" data-testid="edit-inst-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="High School">High School</SelectItem>
                    <SelectItem value="University">University</SelectItem>
                    <SelectItem value="Professional School">Professional School</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setEditInst(null)}>Cancel</Button>
                <Button type="submit" disabled={savingEdit} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-edit-institution">
                  {savingEdit ? "Saving…" : "Save changes"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Level-2 Admins */}
      <Card className="border-slate-200 dark:border-slate-800 mt-8">
        <div className="p-6 flex items-center justify-between border-b border-slate-200 dark:border-slate-800 flex-wrap gap-3">
          <div>
            <h2 className="font-display font-bold text-xl text-slate-900 dark:text-white flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-blue-600" /> Level-2 Admins
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Level-2 admins generate global voucher codes and can edit any graduate record after upload. Institution admins (Level-3) can only upload.
            </p>
          </div>
          <Dialog open={l2Open} onOpenChange={setL2Open}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 font-display font-semibold" data-testid="create-l2-admin-btn">
                <Plus className="w-4 h-4 mr-2" /> Create Level-2 admin
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Create Level-2 admin</DialogTitle>
                <DialogDescription>They'll be able to generate vouchers and edit any graduate record across all institutions.</DialogDescription>
              </DialogHeader>
              <form onSubmit={createL2Admin} className="space-y-4">
                <div>
                  <Label>Full name</Label>
                  <Input value={l2Form.name} onChange={(e) => setL2Form({ ...l2Form, name: e.target.value })} required data-testid="l2-name-input" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={l2Form.email} onChange={(e) => setL2Form({ ...l2Form, email: e.target.value })} required data-testid="l2-email-input" />
                </div>
                <div>
                  <Label>Temporary password (min 8 chars)</Label>
                  <Input type="text" value={l2Form.password} onChange={(e) => setL2Form({ ...l2Form, password: e.target.value })} minLength={8} required data-testid="l2-password-input" />
                  <p className="text-xs text-slate-500 mt-1">Share this with them privately. They can change it via "Forgot password?".</p>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={savingL2} className="bg-blue-600 hover:bg-blue-700" data-testid="l2-submit-btn">
                    {savingL2 ? "Creating…" : "Create admin"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={4} className="p-0">
                  <SkeletonTable rows={2} cols={3} testId="l2-admins-skeleton" />
                </TableCell>
              </TableRow>
            )}
            {!loading && l2Admins.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-slate-500 py-8">
                  No Level-2 admins yet.
                </TableCell>
              </TableRow>
            )}
            {!loading && l2Admins.map((a) => (
              <TableRow key={a.id} data-testid={`l2-row-${a.id}`}>
                <TableCell className="font-medium">{a.name}</TableCell>
                <TableCell className="text-sm text-slate-600 dark:text-slate-300">{a.email}</TableCell>
                <TableCell><span className="inline-block px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-medium">Level-2</span></TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => deleteL2Admin(a.id, a.email)} data-testid={`l2-delete-${a.id}-btn`}>
                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      {/* Certificate design dialog */}
      <CertificateDesignDialog
        institution={designInst}
        open={!!designInst}
        onOpenChange={(o) => !o && setDesignInst(null)}
        onSaved={(updated) => {
          setInstitutions((list) => list.map((i) => (i.id === updated.id ? { ...i, ...updated } : i)));
          setDesignInst(updated);
        }}
      />

      {/* Staff manager dialog */}
      <StaffManagerDialog open={staffOpen} onOpenChange={setStaffOpen} />
    </DashboardLayout>
  );
}

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <Card className="p-7 border-slate-200 hover:border-slate-300 transition-colors" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{label}</div>
          <div className="mt-3 font-display font-extrabold text-4xl text-slate-900 tracking-tight">{value}</div>
        </div>
        <div className="w-12 h-12 rounded-md bg-blue-50 border border-blue-100 flex items-center justify-center">
          <Icon className="w-5 h-5 text-blue-600" strokeWidth={2.5} />
        </div>
      </div>
    </Card>
  );
}

function Field({ label, value, onChange, required, type = "text", testarea, testId }) {
  return (
    <div>
      <Label className="text-sm font-medium text-slate-600 mb-1.5 block">{label}</Label>
      {testarea ? (
        <Textarea value={value} onChange={(e) => onChange(e.target.value)} required={required} className="border-slate-200" data-testid={testId} />
      ) : (
        <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} className="h-11 border-slate-200" data-testid={testId} />
      )}
    </div>
  );
}

function FileDrop({ label, icon: Icon, hint, accept, file, onFile, testId }) {
  return (
    <div>
      <Label className="text-sm font-medium text-slate-600 mb-1.5 block">{label}</Label>
      <label className="flex items-start gap-3 p-3 border border-dashed border-slate-300 rounded-md cursor-pointer hover:border-slate-400 hover:bg-slate-50 transition-colors">
        <div className="w-9 h-9 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-slate-600" />
        </div>
        <div className="min-w-0 flex-1">
          {file ? (
            <>
              <div className="text-sm font-medium text-slate-900 truncate">{file.name}</div>
              <div className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB · click to replace</div>
            </>
          ) : (
            <>
              <div className="text-sm font-medium text-slate-700">Click to upload</div>
              <div className="text-xs text-slate-500">{hint}</div>
            </>
          )}
        </div>
        <input
          type="file"
          className="hidden"
          accept={accept}
          onChange={(e) => onFile(e.target.files?.[0] || null)}
          data-testid={testId}
        />
      </label>
    </div>
  );
}
